import { BasicProgress, GenericProgressStatus, HistoryEntryType } from '../models';
import { LearnizInteractivityType } from './learniz';
import { Game } from '../games/game';

export class LearnizProgression implements BasicProgress {
  percentage: number;
  //not respect single source of truth cause routing use index and logic use slug
  currentChapterIndex?: number;
  currentSlideIndex?: number;
  currentChapterSlug?: string;
  currentSlideSlug?: string;
  chapters: LearnizProgressionChapter[];
  time: number;
  // Score is only relevant for learniz containing games
  score?: number;
  status: GenericProgressStatus;

  /**
   * correspond to the productPerimeterSlug
   * perimeter info is useful for data analysis and stats
   * nice-to-have: have both productSlug and productPerimeterSlug
   */
  slug: string;

  publishedDate?: string;

  constructor(progression?: LearnizProgression) {
    this.slug = progression?.slug || null;
    this.status = progression?.status || GenericProgressStatus.NOT_STARTED;
    this.score = progression?.score;
    this.percentage = progression?.percentage ?? 0;
    this.time = progression?.time ?? 0;
    this.currentChapterIndex = +(progression?.currentChapterIndex || 0);
    this.currentSlideIndex = +(progression?.currentSlideIndex || 0);
    this.currentChapterSlug = progression?.currentChapterSlug ?? '';
    this.currentSlideSlug = progression?.currentSlideSlug ?? '';
    this.chapters = (progression?.chapters || []).map(c => new LearnizProgressionChapter(c));
    this.publishedDate = progression?.publishedDate;
  }
}

export abstract class LearnizProgressionElement {
  slug: string;
  status: GenericProgressStatus;
  score?: number;
  weight?: number;

  protected constructor(learnizProgressionElement?: LearnizProgressionElement) {
    this.weight = learnizProgressionElement?.weight;
    this.slug = learnizProgressionElement?.slug;
    this.status = learnizProgressionElement?.status || GenericProgressStatus.NOT_STARTED;
    this.score = learnizProgressionElement?.score;
  }
}

export class LearnizProgressionInteractivity<T = any> extends LearnizProgressionElement {
  interactivityIndex: number;
  type: LearnizInteractivityType;
  gameType?: Game;
  data?: T;

  constructor(progressionInteractivity?: LearnizProgressionInteractivity) {
    if (progressionInteractivity) {
      super(progressionInteractivity);
      this.status = progressionInteractivity.status || GenericProgressStatus.NOT_STARTED;
      this.interactivityIndex = progressionInteractivity.interactivityIndex;
      this.data = progressionInteractivity.data || null;
      this.type = progressionInteractivity.type || null;
      this.gameType = progressionInteractivity.gameType || null;
    } else {
      super();
      this.score = null;
      this.status = GenericProgressStatus.NOT_STARTED;
      this.interactivityIndex = null;
      this.data = null;
      this.type = null;
      this.gameType = null;
    }
  }
}

export abstract class HistoryEntry {
  slug: string;
  productPerimeterSlug?: string;
  eventDate?: Date;
  time?: number;
  status?: GenericProgressStatus;
  percentage?: number;
  type: HistoryEntryType;
  score?: number;
  badge?: string;
  extra?: any;

  constructor(historyEntry: HistoryEntry) {
    this.slug = historyEntry.slug || null;
    this.productPerimeterSlug = historyEntry.productPerimeterSlug || null;
    this.eventDate = historyEntry.eventDate ?? new Date();
    this.time = historyEntry.time ?? null;
    this.status = historyEntry.status || null;
    this.percentage = historyEntry.percentage || null;
    this.type = historyEntry.type || null;
    this.score = historyEntry.score || null;
    this.badge = historyEntry.badge || null;
    this.extra = historyEntry.extra || null;
  }
}

export class LearnizHistoryEntry extends HistoryEntry {}

export class LearnizProgressionChapter extends LearnizProgressionElement {
  chapterIndex: number;
  slides: LearnizProgressionSlide[];
  percentage: number;

  constructor(learnizProgressionChapter?: LearnizProgressionChapter) {
    if (learnizProgressionChapter) {
      super(learnizProgressionChapter);
      this.chapterIndex = learnizProgressionChapter.chapterIndex;
      this.slides = (learnizProgressionChapter.slides || []).map(s => new LearnizProgressionSlide(s));
      this.percentage = learnizProgressionChapter.percentage || 0;
    } else {
      super();
      this.chapterIndex = null;
      this.slides = [];
      this.percentage = null;
    }
  }
}

export interface LearnizProgressionStats {
  stats: {
    nbVideo: number;
    nbPodcast: number;
    nbGames: number;
    nbDownload: number;
  };
}

export class LearnizProgressionSlide extends LearnizProgressionElement {
  slideIndex: number;
  interactivities: LearnizProgressionInteractivity[];
  scrolled: boolean;

  constructor(learnizProgressionSlide?: LearnizProgressionSlide) {
    if (learnizProgressionSlide) {
      super(learnizProgressionSlide);
      this.slideIndex = learnizProgressionSlide.slideIndex;
      this.scrolled = learnizProgressionSlide.scrolled || false;
      this.interactivities = (learnizProgressionSlide.interactivities || []).map(
        i => new LearnizProgressionInteractivity(i),
      );
    } else {
      super();
      this.scrolled = false;
      this.slideIndex = null;
      this.interactivities = [];
    }
  }
}
